<template>
  <DSFormField
    :id="id"
    :name="props.name"
    :label="props.label ?? undefined"
    :inline-edit="props.inlineEdit"
    :tooltip="props.tooltip"
    :hidden-label="props.hiddenLabel"
    :required="props.required"
    :disabled="props.disabled"
    :error-messages="dsFormField.errorMessages.value"
  >
    <div
      :class="rootClasses"
      data-cy="ds-number-field"
    >
      <div :class="DS_NUMBER_FIELD_CLASSES.InputRow">
        <DSBaseInput
          :id="id"
          ref="numberInputRef"
          v-model="inputValue"
          :type="DS_BASE_INPUT_TYPE.Text"
          :class="DS_NUMBER_FIELD_CLASSES.Input"
          :data-cy-suffix="props.name"
          :placeholder="props.placeholder"
          :inline-edit="props.inlineEdit"
          :disabled="props.disabled"
          :readonly="props.readonly"
          :required="props.required"
          :autocomplete="props.autocomplete"
          :max="props.max"
          :min="props.min"
          :step="props.step"
          :prefix="props.prefix"
          :suffix="props.suffix"
          :clearable="isClearable"
          :has-error="hasErrors"
          :aria-invalid="hasErrors ? 'true' : undefined"
          @update:model-value="onInput"
          @clear="onClear"
          @focus="onFocus"
          @blur="onBlur"
        >
          <template #suffix>
            <DSNumberFieldStepper
              v-if="showStepperControls"
              :controls="stepperControls"
              @step="stepBy"
            />
          </template>
        </DSBaseInput>
      </div>

      <DSNumberFieldHiddenInput
        :name="props.name"
        :value="hiddenValue"
        :disabled="props.disabled"
      />
    </div>
  </DSFormField>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import DSBaseInput from "@/src/design_system/form_elements/base_input/components/DSBaseInput.vue";
import { DS_BASE_INPUT_TYPE } from "@/src/design_system/form_elements/base_input/types/inputType";
import DSFormField from "@/src/design_system/form_elements/form_field/components/DSFormField.vue";
import DSNumberFieldHiddenInput from "@/src/design_system/form_elements/number_field/components/DSNumberFieldHiddenInput.vue";
import DSNumberFieldStepper from "@/src/design_system/form_elements/number_field/components/DSNumberFieldStepper.vue";
import { useDSFormField } from "@/src/design_system/form_elements/shared/composables/useDSFormField";
import { useDSFormFieldId } from "@/src/design_system/form_elements/shared/composables/useDSFormFieldId";
import { useDSInput } from "@/src/design_system/form_elements/shared/composables/useDSInput";
import { DS_NUMBER_FIELD_CLASSES } from "@/src/design_system/form_elements/number_field/constants/classes";
import {
  type DSNumberFieldEmits,
  type DSNumberFieldModelValue,
  type DSNumberFieldProps,
} from "@/src/design_system/form_elements/number_field/types";
import {
  formatNumberFieldValue,
  normalizeNumberFieldValue,
  parseNumberFieldValue,
} from "@/src/design_system/form_elements/number_field/utils/numberFieldValue";
import { DS_STATE_CLASSES } from "@/src/design_system/shared/constants/stateClasses";

const props = withDefaults(defineProps<DSNumberFieldProps>(), {
  hiddenLabel: false,
  inlineEdit: false,
  disabled: false,
  readonly: false,
  required: false,
  tooltip: undefined,
  clearable: undefined,
  allowNegative: false,
  showControls: undefined,
  errorMessages: undefined,
  rules: undefined,
});

const modelValue = defineModel<DSNumberFieldModelValue>({ default: null });
const emit = defineEmits<DSNumberFieldEmits>();

const numberInputRef = ref<InstanceType<typeof DSBaseInput>>();
const { focus, blur } = useDSInput(numberInputRef);

const dsFormField = useDSFormField<DSNumberFieldModelValue>({
  name: computed(() => props.name),
  errorMessages: computed(() => props.errorMessages),
  value: modelValue,
  rules: computed(() => props.rules),
});

const { id } = useDSFormFieldId({ props });

const hasErrors = computed(() => !!dsFormField.errorMessages.value.length);
const isFocused = ref(false);
const numberValueOptions = computed(() => ({
  min: props.min,
  max: props.max,
  precision: props.precision,
  allowNegative: props.allowNegative,
}));
const inputValue = ref(formatNumberFieldValue(modelValue.value, numberValueOptions.value));

watch([modelValue, numberValueOptions], ([value]) => {
  if (!isFocused.value) {
    inputValue.value = formatNumberFieldValue(value, numberValueOptions.value);
  }
});

const hiddenValue = computed(() => modelValue.value ?? "");
const isInteractive = computed(() => !props.disabled && !props.readonly);
const showStepperControls = computed(() => props.showControls ?? true);
const isClearable = computed(() => isInteractive.value && (props.clearable ?? true));
const currentNumber = computed(() => (typeof modelValue.value === "number" ? modelValue.value : null));
const stepValue = computed(() => Math.max(props.step ?? 1, 1));

const canDecrement = computed(() => {
  if (!isInteractive.value) {
    return false;
  }

  const nextValue = normalizeNumberFieldValue((currentNumber.value ?? 0) - stepValue.value, numberValueOptions.value);
  return currentNumber.value === null ? nextValue !== 0 : nextValue !== currentNumber.value;
});

const canIncrement = computed(() => {
  if (!isInteractive.value) {
    return false;
  }

  const nextValue = normalizeNumberFieldValue((currentNumber.value ?? 0) + stepValue.value, numberValueOptions.value);
  return currentNumber.value === null ? nextValue !== 0 : nextValue !== currentNumber.value;
});

const stepperControls = computed(() => ({
  canIncrement: canIncrement.value,
  canDecrement: canDecrement.value,
}));

const rootClasses = computed(() => ({
  [DS_NUMBER_FIELD_CLASSES.Root]: true,
  [DS_STATE_CLASSES.Disabled]: props.disabled,
  [DS_STATE_CLASSES.Error]: hasErrors.value,
}));

function onInput(value: string | number) {
  inputValue.value = String(value);
  modelValue.value = parseNumberFieldValue(inputValue.value, numberValueOptions.value);
  dsFormField.clearFieldErrors();
}

function onClear() {
  if (!isInteractive.value) {
    return;
  }

  inputValue.value = "";
  modelValue.value = null;
  dsFormField.clearFieldErrors();
}

function onFocus(event: FocusEvent) {
  isFocused.value = true;
  emit("focus", event);
}

function onBlur(event: FocusEvent) {
  isFocused.value = false;
  inputValue.value = formatNumberFieldValue(modelValue.value, numberValueOptions.value);
  emit("blur", event);
}

function stepBy(direction: -1 | 1) {
  if (!isInteractive.value) {
    return;
  }

  const nextValue = normalizeNumberFieldValue(
    (currentNumber.value ?? 0) + stepValue.value * direction,
    numberValueOptions.value
  );
  modelValue.value = nextValue;
  inputValue.value = formatNumberFieldValue(nextValue, numberValueOptions.value);
  dsFormField.clearFieldErrors();
}

defineExpose({
  focus,
  blur,
});
</script>
